<template>
    <div
        @click="onClick"
        @dblclick="onDblclick"
        class="module"
        :class="classes"
        v-show="visible && loaded"
    >
        <component
            :is="component"
            @loaded="ready"
        />
        <div
            v-if="blockInput"
            class="module__input-blocker"
        ></div>
        <teleport
            v-if="app === 'cms' && form"
            to="body"
        >
            <Dialogue
                v-model="editing"
                :btns="dialogueBtns"
                @update="update"
            >
                <template v-slot:content>
                    <component :is="form"/>
                </template>
            </Dialogue>
        </teleport>
    </div>
</template>

<script>
import {v4 as uuidv4} from 'uuid';
import {inject, provide} from "vue";
import Kernel from "@oilstone/kernel";
import {accessesData, boot, hasEvents, hydratesData, module, mutatesData, persistsData} from "../composers";
import Factory from "../factory";

export default {
    props: {
        type: String,
        id: null,
        resource: Object,
        sort: Number,
        pointerEvents: {
            type: Boolean,
            default: true
        }
    },

    setup(props) {
        const app = Kernel.resolve('context').app;
        const settings = Kernel.resolve('moduleRegistry').find(props.type);
        const uuid = uuidv4();
        const scope = Factory.scope(settings.config);

        boot(settings.schema, scope, uuid, inject('parentUuid', null));
        provide('uuid', uuid);
        provide('parentUuid', uuid);
        provide('resource', {
            type: inject('type', null),
            uuid: inject('uuid', null)
        });

        const {focussed, enableFocus, disableFocus, editing, enableEditing, disableEditing} = module(uuid);
        const {id: moduleId, parentId, item} = accessesData(uuid);
        const {listen, fire} = hasEvents(uuid);
        const {load, hydrate} = hydratesData(uuid);
        const {make, setSort, dispatchChanges} = mutatesData(uuid);
        const {overwrite, destroy} = persistsData(uuid);

        return {
            app,
            listen,
            fire,
            uuid,
            item,
            moduleId,
            parentId,
            focussed,
            enableFocus,
            disableFocus,
            editing,
            enableEditing,
            disableEditing,
            load,
            make,
            dispatchChanges,
            hydrate,
            setSort,
            saveRecord: overwrite,
            destroy,
            component: settings.config.components.root.name,
            form: settings.config.components.form ? settings.config.components.form.name : null,
        }
    },

    data() {
        return {
            removed: false,
            loaded: false,
            dialogueBtns: [
                {
                    text: 'Update',
                    click: 'update'
                }
            ]
        }
    },

    watch: {
        id() {
            this.load(this.id);
        },

        sort() {
            this.applySort();
        },
    },

    computed: {
        classes() {
            const classes = [`module-${this.type}`];

            if (this.app === 'cms') {
                classes.push('module--editable');

                if (this.pointerEvents) {
                    classes.push('module--pointer-events-enabled');
                }
            }

            if (this.focussed) {
                classes.push('module--focussed');
            }

            return classes;
        },

        blockInput() {
            return this.app === 'cms';
        },

        isNew() {
            return !this.moduleId;
        },

        visible() {
            return this.isNew || this.type === 'container' || this.parentId !== null;
        }
    },

    methods: {
        ready() {
            if (this.resource) {
                this.hydrate(this.resource);

                if (!this.visible) {
                    this.removed = true;
                }

                this.loaded = true;
                return;
            }

            this.make();

            if (this.id) {
                this.load(this.id);

                if (!this.visible) {
                    this.removed = true;
                }

                this.loaded = true;
                return;
            } else {
                this.fire('madenew');
            }

            this.loaded = true;
            this.applySort();
        },

        applySort() {
            this.setSort(this.sort);
        },

        onClick(e) {
            if (this.app === 'cms') {
                if (this.pointerEvents) {
                    this.enableFocus(e);
                }
            }
        },

        onDblclick(e) {
            if (this.app === 'cms') {
                if (this.pointerEvents) {
                    this.enableEditing(e);
                }
            }
        },

        update() {
            this.dispatchChanges();
            this.disableEditing();
        },

        save(payload) {
            const parentId = parseInt(payload.item.id);
            const module = Object.assign({}, this.item(this.uuid).value || {}, {
                parentId
            });

            if (this.removed && !this.isNew) {
                this.destroy();
                return;
            }

            this.saveRecord(module);
        },
    },

    mounted() {
        this.listen({
            parentmoduleSaved: (payload) => {
                this.save(payload);
            }
        });

        window.addEventListener('keyup', (e) => {
            if (!this.focussed) {
                return false;
            }

            if (e.key === "Delete" || e.key === "Backspace") {
                this.removed = true;
                this.$emit('remove');
            }
        });
    }
}
</script>

<style>
.module {
    @apply relative my-12;
    outline-offset: 5px;

    &--pointer-events-enabled {
        &:hover {
            @apply cursor-pointer;
            outline: dashed 1px cornflowerblue;
        }

        .module__input-blocker {
            @apply absolute top-0 left-0 w-full h-full;
            z-index: 999;
        }
    }

    &--focussed {
        outline: solid 1px cornflowerblue !important;
    }
}
</style>
